import { useEffect } from "react";
import config from "../config";
import logger from "../utils/logger";

/**
 * Custom hook to fetch and cache patron data for credentials
 * 
 * Automatically fetches patron information when new credentials are loaded,
 * caching results to avoid duplicate API calls.
 * 
 * @param {Array} credentials - Array of credential objects
 * @param {Object} patronsMap - Map of patronId to patron data
 * @param {Function} setPatronsMap - Function to update the patrons map
 * @param {Function} authenticatedFetch - Authenticated fetch function
 */
export const usePatronData = (
  credentials,
  patronsMap,
  setPatronsMap,
  authenticatedFetch
) => {
  useEffect(() => {
    const fetchPatronData = async () => {
      const patronIds = credentials
        .map((cred) => cred.patronId)
        .filter((id) => id && !patronsMap[id]); // Only fetch patrons we don't have yet

      if (patronIds.length === 0) return;

      const uniquePatronIds = [...new Set(patronIds)];

      for (const patronId of uniquePatronIds) {
        try {
          const cred = credentials.find((c) => c.patronId === patronId);
          if (!cred || !cred.orgId) {
            logger.warn(
              `Credential with patronId ${patronId} is missing orgId`
            );
            continue;
          }

          logger.log(
            `Fetching patron data for patronId=${patronId}, orgId=${cred.orgId}`
          );
          const response = await authenticatedFetch(
            `${config.apiEndpoint}/patrons/${cred.orgId}/${patronId}`,
            { headers: { "x-api-key": config.apiKey } }
          );

          if (response.ok) {
            const data = await response.json();
            const patron = data.patron || data; // Handle both {patron: {...}} and direct patron object
            logger.log(`Fetched patron data:`, patron);
            setPatronsMap((prev) => ({ ...prev, [patronId]: patron }));
          } else {
            logger.warn(
              `Failed to fetch patron ${patronId}: ${response.status}`
            );
          }
        } catch (error) {
          logger.warn(`Failed to fetch patron ${patronId}:`, error);
        }
      }
    };

    fetchPatronData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials]); // Removed patronsMap from dependencies to avoid infinite loop
};

export default usePatronData;
